import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Order } from '@models/order';
import { OrderService } from '@services/order.service';
import { OrderEditDialogComponent } from '@components/admin/patient/order-edit-dialog/order-edit-dialog.component';
import { hasJobAction } from '@classes/job-actions';
import { FormatData } from '@classes/format-data';
import { stackOpenTasksFromOrder } from '@classes/task-activity';
import {
  StackTodoTask,
  compareTodoTodayByDue,
  todoDueTimestamp,
} from '@classes/stack-board';

export { StackTodoTask, compareTodoTodayByDue, todoDueTimestamp };

@Component({
  selector: 'app-stack-todo-today',
  templateUrl: './stack-todo-today.component.html',
  styleUrls: [
    '../../managed-board.component.css',
    './stack-todo-today.component.css',
  ],
})
export class StackTodoTodayComponent implements OnInit, AfterViewInit {
  @Output() taskCountChange = new EventEmitter<number>();
  @Output() jobsChanged = new EventEmitter<void>();
  @ViewChild(MatSort) sort: MatSort;
  displayedColumns = ['patient', 'invoiceNumber', 'task', 'due', 'view', 'log'];
  tasks: StackTodoTask[] = [];
  dataSource = new MatTableDataSource<StackTodoTask>([]);
  dataLoading = false;

  constructor(
    private orderService: OrderService,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.dataSource.sortingDataAccessor = (row, property) => {
      if (property === 'due') {
        return todoDueTimestamp(row.due);
      }
      if (property === 'task') {
        return String(row.task || '').toUpperCase();
      }
      if (property === 'patient') {
        return this.patientName(row.order).toUpperCase();
      }
      if (property === 'invoiceNumber') {
        return this.invoiceLabel(row.order).toUpperCase();
      }
      return (row as any)[property];
    };
    this.loadTasks();
  }

  ngAfterViewInit(): void {
    this.attachSort();
  }

  loadTasks(): void {
    this.dataLoading = true;
    this.orderService.getNewOrders('todo-today').subscribe((vendorOrders) => {
      const orders = (vendorOrders || []).flatMap(
        (vendorOrder) => vendorOrder.orders || []
      );
      this.tasks = orders
        .flatMap((order) => this.toTasks(order))
        .sort(compareTodoTodayByDue);
      this.dataSource.data = this.tasks;
      this.dataLoading = false;
      this.taskCountChange.emit(this.tasks.length);
      this.cdRef.detectChanges();
      this.attachSort();
    });
  }

  private attachSort(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  patientName(order: Order): string {
    const patient: any = order?.patient;
    if (!patient) {
      return '—';
    }
    const last = String(patient.lastName || '').trim();
    const first = String(patient.firstName || '').trim();
    return [last, first].filter(Boolean).join(', ') || '—';
  }

  invoiceLabel(order: Order): string {
    const invoice = FormatData.displayInvoiceNumber(order?.invoiceNumber);
    const type = String(order?.type || '').trim();
    if (!invoice) {
      return '—';
    }
    return type ? `${type} – ${invoice}` : invoice;
  }

  openOrder(order: Order): void {
    const patient: any = order?.patient;
    const patientId =
      (typeof patient === 'object' && patient?._id) || patient || null;
    const orderId = order?._id;
    if (!patientId || !orderId) {
      return;
    }
    const dialogRef = this.dialog.open(OrderEditDialogComponent, {
      width: '96vw',
      maxWidth: '96vw',
      height: '92vh',
      maxHeight: '92vh',
      autoFocus: false,
      panelClass: 'order-edit-dialog-panel',
      data: { patientId, orderId },
    });
    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.loadTasks();
        this.jobsChanged.emit();
      }
    });
  }

  isShipTask(row: StackTodoTask): boolean {
    return (
      String((row.order as any)?.fulfillment || '') === 'SHIP' &&
      String(row.order?.status || '') !== 'DISPENSED'
    );
  }

  private toTasks(order: Order): StackTodoTask[] {
    const workflow = stackOpenTasksFromOrder(order).map((row) => ({
      order,
      task: row.task,
      due: row.due || this.taskDue(order, row.task),
    }));
    if (workflow.length) {
      return workflow;
    }
    const task = this.classifyTask(order);
    if (!task) {
      return [];
    }
    return [
      {
        order,
        task,
        due: this.taskDue(order, task),
      },
    ];
  }

  private classifyTask(order: Order): string | null {
    const status = String(order?.status || '').trim();
    const todo = String(order?.todo || '').trim();
    const job = String((order as any)?.jobAction || '').trim();
    const jobActions = (order as any)?.jobActions;
    const rxStatus = String(order?.rxStatus || '').trim();
    const fulfillment = String((order as any)?.fulfillment || '').trim();

    if (this.isCallForRxTask(order, status, todo, job, jobActions, rxStatus)) {
      return 'CALL FOR RX';
    }
    if (this.isFrameEtaTask(order, status)) {
      return 'CHECK FRAME ETA';
    }
    if (fulfillment === 'SHIP' && status === 'RECEIVED') {
      return 'SHIP JOB';
    }
    if (status === 'INQUIRY') {
      return 'INQUIRY';
    }
    if (this.isCustomerToOrder(order, status, todo)) {
      return 'TO ORDER';
    }
    return null;
  }

  private isCallForRxTask(
    order: Order,
    status: string,
    todo: string,
    job: string,
    jobActions: string[] | null | undefined,
    rxStatus: string
  ): boolean {
    if (
      status === 'CALL FOR RX' ||
      todo === 'CALL' ||
      rxStatus === 'CALL FOR RX' ||
      hasJobAction(job, jobActions, 'CALL FOR RX')
    ) {
      return true;
    }
    if (todo !== 'HOLD' && status !== 'HOLD/RX' && status !== 'HOLD / RX') {
      return false;
    }
    return this.isFollowUpDue(order);
  }

  private isFollowUpDue(order: Order): boolean {
    const followUp = (order as any)?.products?.[0]?.frame?.followUpDate;
    if (!followUp) {
      return false;
    }
    const parsed = followUp instanceof Date ? followUp : new Date(followUp);
    if (isNaN(parsed.getTime())) {
      return false;
    }
    parsed.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return parsed.getTime() <= today.getTime();
  }

  private isFrameEtaTask(order: Order, status: string): boolean {
    if (
      ![
        'AWAITING FRAME',
        'AWAITING PART',
        'AWAITING ACCESSORY',
        'AWAITING FRAME + LENSES',
        'AWAITING LENSES',
        'AWAITING REPAIR',
      ].includes(status)
    ) {
      return false;
    }
    if ((order as any)?.products?.[0]?.frame?.etaChecked) {
      return false;
    }
    const eta: unknown = order?.eta;
    if (!eta) {
      return false;
    }
    const parsed = eta instanceof Date ? eta : new Date(String(eta));
    if (isNaN(parsed.getTime())) {
      return false;
    }
    parsed.setHours(0, 0, 0, 0);
    const limit = new Date();
    limit.setHours(0, 0, 0, 0);
    limit.setDate(limit.getDate() + 7);
    return parsed.getTime() <= limit.getTime();
  }

  private isCustomerToOrder(order: Order, status: string, todo: string): boolean {
    if (todo === 'HOLD' || todo === 'CALL' || todo === 'REPAIR' || todo === 'SAVE') {
      return false;
    }
    if (
      [
        'INQUIRY',
        'QUOTE',
        'CALL FOR RX',
        'HOLD/RX',
        'HOLD / RX',
        'HOLD',
        'AWAITING FRAME',
        'AWAITING PART',
        'AWAITING ACCESSORY',
        'AWAITING FRAME + LENSES',
        'AWAITING LENSES',
        'AWAITING REPAIR',
        'TO REPAIR',
        'DISPENSED',
        'RECEIVED',
      ].includes(status)
    ) {
      return false;
    }
    const source = String(
      (order as any)?.products?.[0]?.frame?.frameSource || ''
    ).trim();
    return !!source && source !== 'STOCK';
  }

  private taskDue(order: Order, task: string): string | Date {
    const frame = (order as any)?.products?.[0]?.frame || {};
    if (task === 'CALL FOR RX') {
      return frame.followUpDate || order?.date;
    }
    if (task === 'CHECK FRAME ETA') {
      return order?.eta || frame.dueDate || order?.date;
    }
    if (task === 'SHIP JOB') {
      return frame.dateReceived || order?.date;
    }
    return order?.eta || order?.date;
  }

}
